import { useState, useEffect } from 'react';
import {
  ArrowLeft, Phone as PhoneIcon, MessageCircle, Edit3, Trash2,
  Plus, ChevronRight, IndianRupee, Package, Truck,
  CheckCircle2, Clock, AlertTriangle, X, Ruler,
} from 'lucide-react';
import Swal from 'sweetalert2';

const STAGES = [
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle2, color: 'blue' },
  { key: 'fabric_sourcing', label: 'Fabric', icon: Package, color: 'purple' },
  { key: 'at_karigar', label: 'Karigar', icon: Clock, color: 'amber' },
  { key: 'stitching_done', label: 'Stitched', icon: CheckCircle2, color: 'cyan' },
  { key: 'fitting_qc', label: 'QC', icon: Ruler, color: 'indigo' },
  { key: 'ready', label: 'Ready', icon: Package, color: 'emerald' },
  { key: 'delivered', label: 'Delivered', icon: Truck, color: 'gray' },
];

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'card', label: 'Card' },
  { value: 'bank_transfer', label: 'Bank' },
  { value: 'other', label: 'Other' },
];

const OrderDetail = ({ authAxios, orderId, onBack, onRefresh }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_mode: 'cash', reference: '', notes: '' });
  const [stageForm, setStageForm] = useState({ stage: '', notes: '', karigar_name: '', expected_delivery_date: '' });
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const res = await authAxios.get(`/api/order-book/orders/${orderId}/`);
      setOrder(res.data);
    } catch (err) {
      console.error('Error fetching order:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrder(); }, [orderId]);

  const handleAddPayment = async () => {
    if (!paymentForm.amount || Number(paymentForm.amount) <= 0) return;
    try {
      setSaving(true);
      const res = await authAxios.post(`/api/order-book/orders/${orderId}/payments/`, paymentForm);
      setOrder(res.data);
      setShowPaymentForm(false);
      setPaymentForm({ amount: '', payment_mode: 'cash', reference: '', notes: '' });
      onRefresh();
    } catch (err) {
      console.error('Error adding payment:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    const result = await Swal.fire({
      title: 'Delete Payment?', text: 'This will remove this payment record.',
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#111827', cancelButtonColor: '#d1d5db', confirmButtonText: 'Yes, delete',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await authAxios.delete(`/api/order-book/orders/${orderId}/payments/${paymentId}/`);
      setOrder(res.data);
      onRefresh();
    } catch (err) {
      console.error('Error deleting payment:', err);
    }
  };

  const handleUpdateStage = async () => {
    if (!stageForm.stage) return;
    try {
      setSaving(true);
      const payload = { stage: stageForm.stage, notes: stageForm.notes };
      if (stageForm.karigar_name) payload.karigar_name = stageForm.karigar_name;
      if (stageForm.expected_delivery_date) payload.expected_delivery_date = stageForm.expected_delivery_date;
      const res = await authAxios.post(`/api/order-book/orders/${orderId}/stage/`, payload);
      setOrder(res.data);
      setShowStageModal(false);
      setStageForm({ stage: '', notes: '', karigar_name: '', expected_delivery_date: '' });
      onRefresh();
    } catch (err) {
      console.error('Error updating stage:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      const res = await authAxios.put(`/api/order-book/orders/${orderId}/`, editForm);
      setOrder(res.data);
      setEditing(false);
      onRefresh();
    } catch (err) {
      console.error('Error updating order:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Delete Order?', text: 'This will permanently delete this order and all payments.',
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#dc2626', cancelButtonColor: '#d1d5db', confirmButtonText: 'Yes, delete',
    });
    if (!result.isConfirmed) return;
    try {
      await authAxios.delete(`/api/order-book/orders/${orderId}/`);
      onRefresh();
      onBack();
    } catch (err) {
      console.error('Error deleting order:', err);
    }
  };

  const inputClass = "w-full px-4 py-3.5 border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent";

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-10 h-10 border-[3px] border-gray-200 border-t-gray-800 rounded-full animate-spin" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500 text-base">Order not found</p>
        <button onClick={onBack} className="mt-4 text-base text-gray-900 font-semibold underline">Go back</button>
      </div>
    );
  }

  const currentStageIndex = STAGES.findIndex(s => s.key === order.current_stage);
  const isOverdue = order.expected_delivery_date &&
    order.current_stage !== 'delivered' && order.current_stage !== 'cancelled' &&
    new Date(order.expected_delivery_date) < new Date();

  const paidPercent = Math.min((Number(order.amount_paid) / Number(order.total_amount)) * 100, 100) || 0;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button onClick={onBack} className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-xl active:scale-95 transition-all">
          <ArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-bold text-gray-900 tracking-tight truncate">{order.customer_name}</h2>
          <p className="text-sm text-gray-500 font-medium">{order.order_number}</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex items-center gap-2 mb-5">
        {order.customer_phone && (
          <>
            <a href={`tel:${order.customer_phone}`}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 active:scale-95 transition-all shadow-sm">
              <PhoneIcon className="w-4 h-4" /> Call
            </a>
            <a href={`https://wa.me/91${order.customer_phone.replace(/\D/g, '').slice(-10)}`}
              target="_blank" rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm font-semibold text-emerald-700 active:scale-95 transition-all">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          </>
        )}
        <button onClick={() => { setEditForm({ notes: order.notes, karigar_name: order.karigar_name, expected_delivery_date: order.expected_delivery_date || '', priority: order.priority }); setEditing(true); }}
          className="w-11 h-11 flex items-center justify-center bg-white border border-gray-200 rounded-xl active:scale-95 transition-all shadow-sm">
          <Edit3 className="w-4 h-4 text-gray-600" />
        </button>
        <button onClick={handleDelete}
          className="w-11 h-11 flex items-center justify-center bg-red-50 border border-red-200 rounded-xl active:scale-95 transition-all">
          <Trash2 className="w-4 h-4 text-red-500" />
        </button>
      </div>

      {/* Overdue Alert */}
      {isOverdue && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-5 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700 font-semibold">
            Overdue — expected {new Date(order.expected_delivery_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
        </div>
      )}

      {/* ─── Delivery Pipeline ─── */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-4 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-gray-900">Delivery Pipeline</h3>
          <button
            onClick={() => {
              const nextStage = STAGES[currentStageIndex + 1];
              setStageForm({ stage: nextStage?.key || '', notes: '', karigar_name: order.karigar_name || '', expected_delivery_date: order.expected_delivery_date || '' });
              setShowStageModal(true);
            }}
            disabled={order.current_stage === 'delivered' || order.current_stage === 'cancelled'}
            className="text-xs font-semibold text-white bg-gray-900 hover:bg-gray-800 px-3.5 py-2 rounded-lg active:scale-95 transition-all disabled:opacity-30">
            Next Stage →
          </button>
        </div>

        {/* Pipeline Steps */}
        <div className="flex items-center gap-0.5 overflow-x-auto pb-2">
          {STAGES.map((stage, i) => {
            const isCompleted = i < currentStageIndex;
            const isCurrent = i === currentStageIndex;
            return (
              <div key={stage.key} className="flex items-center">
                <div className={`px-2.5 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                  isCurrent ? 'bg-gray-900 text-white' :
                  isCompleted ? 'bg-emerald-100 text-emerald-700' :
                  'bg-gray-50 text-gray-300'
                }`}>
                  {isCompleted ? '✓' : ''} {stage.label}
                </div>
                {i < STAGES.length - 1 && (
                  <ChevronRight className={`w-3.5 h-3.5 mx-0.5 flex-shrink-0 ${isCompleted ? 'text-emerald-400' : 'text-gray-200'}`} />
                )}
              </div>
            );
          })}
        </div>

        {order.karigar_name && (
          <div className="mt-3 text-sm text-gray-500 font-medium">
            Karigar: <span className="text-gray-800 font-semibold">{order.karigar_name}</span>
          </div>
        )}

        {/* Stage History */}
        {order.stage_history?.length > 0 && (
          <div className="mt-4 border-t border-gray-100 pt-3">
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">History</h4>
            <div className="space-y-1.5">
              {order.stage_history.map((entry, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <span className="w-2 h-2 rounded-full bg-gray-300 mt-1.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold text-gray-700">{entry.stage_display}</span>
                    {entry.notes && <span className="text-gray-500"> — {entry.notes}</span>}
                    <span className="text-gray-400 block text-xs mt-0.5">
                      {new Date(entry.changed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ─── Payment Tracking ─── */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-4 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-gray-900">Payments</h3>
          <button onClick={() => setShowPaymentForm(true)}
            className="flex items-center gap-1.5 text-xs font-semibold text-white bg-gray-900 hover:bg-gray-800 px-3.5 py-2 rounded-lg active:scale-95 transition-all">
            <Plus className="w-3.5 h-3.5" /> Add Payment
          </button>
        </div>

        {/* Payment Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-500 font-medium">Paid: <span className="text-gray-800 font-semibold">₹{Number(order.amount_paid).toLocaleString('en-IN')}</span></span>
            <span className="font-semibold text-gray-900">Total: ₹{Number(order.total_amount).toLocaleString('en-IN')}</span>
          </div>
          <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                order.payment_status === 'paid' ? 'bg-emerald-500' :
                order.payment_status === 'partial' ? 'bg-amber-500' : 'bg-gray-300'
              }`}
              style={{ width: `${paidPercent}%` }}
            />
          </div>
          {Number(order.balance) > 0 && (
            <div className="text-sm text-amber-600 font-semibold mt-1.5">
              Balance: ₹{Number(order.balance).toLocaleString('en-IN')}
            </div>
          )}
        </div>

        {/* Payment Entries */}
        {order.payments?.length > 0 ? (
          <div className="space-y-2">
            {order.payments.map((p) => (
              <div key={p.id} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3.5 group">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <IndianRupee className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-base font-semibold text-gray-900">₹{Number(p.amount).toLocaleString('en-IN')}</div>
                  <div className="text-xs text-gray-500 font-medium">
                    {p.payment_mode?.toUpperCase()} · {new Date(p.payment_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {p.reference && ` · ${p.reference}`}
                  </div>
                </div>
                <button onClick={() => handleDeletePayment(p.id)}
                  className="w-9 h-9 flex items-center justify-center hover:bg-red-50 rounded-xl active:scale-95 transition-all">
                  <Trash2 className="w-3.5 h-3.5 text-red-400" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 text-center py-4 font-medium">No payments recorded yet</p>
        )}
      </div>

      {/* ─── Products ─── */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-4 shadow-sm">
        <h3 className="text-base font-bold text-gray-900 mb-3">Products</h3>
        {order.products?.length > 0 ? (
          <div className="space-y-2">
            {order.products.map((p, i) => (
              <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3.5">
                {p.image ? (
                  <img src={p.image} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-9 h-9 rounded-lg bg-gray-200 flex items-center justify-center text-sm font-bold text-gray-500 flex-shrink-0">
                    {i + 1}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-base font-semibold text-gray-900">{p.name || 'Item'}</div>
                  <div className="text-xs text-gray-500 font-medium">
                    {[p.size && `Size: ${p.size}`, p.color && `Color: ${p.color}`, p.customizations].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-base font-semibold text-gray-900">₹{Number(p.price || 0).toLocaleString('en-IN')}</div>
                  <div className="text-xs text-gray-500 font-medium">Qty: {p.qty || 1}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 text-center py-4 font-medium">No products listed</p>
        )}
      </div>

      {/* ─── Measurements ─── */}
      {order.measurements && Object.entries(order.measurements).filter(([, v]) => v).length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-4 shadow-sm">
          <h3 className="text-base font-bold text-gray-900 mb-3">Measurements</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {Object.entries(order.measurements).filter(([, v]) => v).map(([key, val]) => (
              <div key={key} className="bg-gray-50 rounded-xl p-3 text-center">
                <div className="text-xl font-bold text-gray-900">{val}"</div>
                <div className="text-xs text-gray-500 font-medium capitalize mt-0.5">{key.replace(/_/g, ' ')}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Notes ─── */}
      {order.notes && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-4 shadow-sm">
          <h3 className="text-base font-bold text-gray-900 mb-2">Notes</h3>
          <p className="text-base text-gray-700 whitespace-pre-wrap leading-relaxed">{order.notes}</p>
        </div>
      )}

      {/* ─── Customer Info ─── */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-4 shadow-sm">
        <h3 className="text-base font-bold text-gray-900 mb-3">Customer Info</h3>
        <div className="space-y-3">
          <div className="flex justify-between"><span className="text-sm text-gray-500 font-medium">Phone</span><span className="text-base font-semibold text-gray-900">{order.customer_phone}</span></div>
          {order.customer_email && <div className="flex justify-between"><span className="text-sm text-gray-500 font-medium">Email</span><span className="text-base text-gray-800">{order.customer_email}</span></div>}
          {order.event_name && <div className="flex justify-between"><span className="text-sm text-gray-500 font-medium">Event</span><span className="text-base text-gray-800">{order.event_name}</span></div>}
          <div className="flex justify-between"><span className="text-sm text-gray-500 font-medium">Priority</span><span className={`text-sm font-semibold px-2.5 py-1 rounded-full ${order.priority === 'hot' ? 'bg-red-100 text-red-600' : order.priority === 'warm' ? 'bg-amber-100 text-amber-600' : 'bg-blue-100 text-blue-600'}`}>{order.priority}</span></div>
          <div className="flex justify-between"><span className="text-sm text-gray-500 font-medium">Created</span><span className="text-base text-gray-800">{new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span></div>
        </div>
      </div>

      {/* ─── Add Payment Modal ─── */}
      {showPaymentForm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end justify-center z-50" onClick={() => setShowPaymentForm(false)}>
          <div className="bg-white rounded-t-3xl w-full max-w-lg max-h-[85vh] overflow-y-auto animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-center pt-3 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
            <div className="px-6 pb-8 pt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Add Payment</h3>
                <button onClick={() => setShowPaymentForm(false)} className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-xl">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Amount *</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-base">₹</span>
                    <input type="number" inputMode="numeric" value={paymentForm.amount}
                      onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                      placeholder="0" autoFocus
                      className="w-full pl-9 pr-4 py-3.5 border border-gray-200 rounded-xl text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-gray-900" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Mode</label>
                  <div className="grid grid-cols-3 gap-2">
                    {PAYMENT_MODES.map(m => (
                      <button key={m.value} type="button"
                        onClick={() => setPaymentForm({ ...paymentForm, payment_mode: m.value })}
                        className={`px-3 py-3 rounded-xl text-sm font-semibold border-2 transition-all ${
                          paymentForm.payment_mode === m.value ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'
                        }`}>
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Reference</label>
                  <input type="text" value={paymentForm.reference}
                    onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                    placeholder="UPI ID, transaction number, etc." className={inputClass} />
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setShowPaymentForm(false)}
                    className="flex-1 px-4 py-3.5 border-2 border-gray-200 text-gray-700 rounded-xl text-base font-semibold hover:bg-gray-50 active:scale-95 transition-all">Cancel</button>
                  <button onClick={handleAddPayment} disabled={saving || !paymentForm.amount}
                    className="flex-1 px-4 py-3.5 bg-gray-900 text-white rounded-xl text-base font-semibold hover:bg-gray-800 disabled:opacity-40 active:scale-95 transition-all">
                    {saving ? 'Adding...' : 'Add Payment'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Stage Update Modal ─── */}
      {showStageModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end justify-center z-50" onClick={() => setShowStageModal(false)}>
          <div className="bg-white rounded-t-3xl w-full max-w-lg max-h-[85vh] overflow-y-auto animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-center pt-3 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
            <div className="px-6 pb-8 pt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Update Stage</h3>
                <button onClick={() => setShowStageModal(false)} className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-xl">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Move to Stage *</label>
                  <div className="grid grid-cols-2 gap-2">
                    {STAGES.filter(s => STAGES.findIndex(st => st.key === s.key) > currentStageIndex).map(s => (
                      <button key={s.key} type="button"
                        onClick={() => setStageForm({ ...stageForm, stage: s.key })}
                        className={`px-3 py-3 rounded-xl text-sm font-semibold border-2 transition-all ${
                          stageForm.stage === s.key ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'
                        }`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                {(stageForm.stage === 'at_karigar' || stageForm.stage === 'fabric_sourcing') && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Karigar Name</label>
                    <input type="text" value={stageForm.karigar_name}
                      onChange={(e) => setStageForm({ ...stageForm, karigar_name: e.target.value })}
                      placeholder="Name of the karigar" className={inputClass} />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Expected Delivery</label>
                  <input type="date" value={stageForm.expected_delivery_date}
                    onChange={(e) => setStageForm({ ...stageForm, expected_delivery_date: e.target.value })}
                    className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
                  <input type="text" value={stageForm.notes}
                    onChange={(e) => setStageForm({ ...stageForm, notes: e.target.value })}
                    placeholder="Optional note" className={inputClass} />
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setShowStageModal(false)}
                    className="flex-1 px-4 py-3.5 border-2 border-gray-200 text-gray-700 rounded-xl text-base font-semibold hover:bg-gray-50 active:scale-95 transition-all">Cancel</button>
                  <button onClick={handleUpdateStage} disabled={saving || !stageForm.stage}
                    className="flex-1 px-4 py-3.5 bg-gray-900 text-white rounded-xl text-base font-semibold hover:bg-gray-800 disabled:opacity-40 active:scale-95 transition-all">
                    {saving ? 'Updating...' : 'Update Stage'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Edit Modal ─── */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end justify-center z-50" onClick={() => setEditing(false)}>
          <div className="bg-white rounded-t-3xl w-full max-w-lg max-h-[85vh] overflow-y-auto animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-center pt-3 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
            <div className="px-6 pb-8 pt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Edit Order</h3>
                <button onClick={() => setEditing(false)} className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-xl">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Karigar</label>
                  <input type="text" value={editForm.karigar_name || ''} onChange={(e) => setEditForm({ ...editForm, karigar_name: e.target.value })} className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Expected Delivery</label>
                  <input type="date" value={editForm.expected_delivery_date || ''} onChange={(e) => setEditForm({ ...editForm, expected_delivery_date: e.target.value })} className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Priority</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['hot', 'warm', 'cold'].map(p => (
                      <button key={p} type="button" onClick={() => setEditForm({ ...editForm, priority: p })}
                        className={`px-3 py-3 rounded-xl text-sm font-semibold border-2 capitalize transition-all ${editForm.priority === p ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'}`}>
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
                  <textarea value={editForm.notes || ''} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={3}
                    className={`${inputClass} resize-none`} />
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setEditing(false)}
                    className="flex-1 px-4 py-3.5 border-2 border-gray-200 text-gray-700 rounded-xl text-base font-semibold hover:bg-gray-50 active:scale-95 transition-all">Cancel</button>
                  <button onClick={handleSaveEdit} disabled={saving}
                    className="flex-1 px-4 py-3.5 bg-gray-900 text-white rounded-xl text-base font-semibold hover:bg-gray-800 disabled:opacity-40 active:scale-95 transition-all">
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default OrderDetail;
